import { Delete, PhotoCamera } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { createProduct, updateProduct } from "../../services/productService";

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string()
    .required("Product name is required")
    .min(3, "Product name must be at least 3 characters")
    .max(255, "Product name must be at most 255 characters"),
  description: Yup.string().nullable(),
  requires_license: Yup.boolean().default(false),
  banner_image: Yup.string().nullable(),
});

// Keeps the input in the layout (and clickable) while hiding it visually.
// `visibility: hidden` / `display: none` would drop it from hit-testing.
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ProductFormDialog = ({ open, product, onClose, onSave }) => {
  const [error, setError] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");
  const [initialValues, setInitialValues] = useState({
    name: "",
    description: "",
    requires_license: false,
    banner_image: "",
  });

  useEffect(() => {
    if (product) {
      setInitialValues({
        name: product.name || "",
        description: product.description || "",
        requires_license: product.requires_license || false,
        banner_image: product.banner_image || "",
      });
      setBannerPreview(product.banner_image || "");
    } else {
      setInitialValues({
        name: "",
        description: "",
        requires_license: false,
        banner_image: "",
      });
      setBannerPreview("");
    }
  }, [product]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let savedProduct;

      if (product) {
        // Update existing product
        savedProduct = await updateProduct(product.id, values);
      } else {
        // Create new product
        savedProduct = await createProduct(values);
      }

      setError("");
      onSave(savedProduct, !product);
    } catch (err) {
      setError(err.message || "Failed to save product. Please try again.");
      console.error("Error saving product:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setError("");
    setBannerPreview("");
    onClose();
  };

  const handleBannerUpload = (event, setFieldValue) => {
    const file = event.target.files[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (file) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(
          "Image size must be less than 10MB. Please choose a smaller image.",
        );
        return;
      }

      // Check file type
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Calculate new dimensions (max width: 1200px, maintain aspect ratio)
          const maxWidth = 1200;
          const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;

          // Draw and compress
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.85);

          setBannerPreview(compressedBase64);
          setFieldValue("banner_image", compressedBase64);
          setError(""); // Clear any previous errors
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveBanner = (setFieldValue) => {
    setBannerPreview("");
    setFieldValue("banner_image", "");
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>{product ? "Edit Product" : "Add New Product"}</DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, setFieldValue, values }) => (
          <Form>
            <DialogContent>
              {error && <Box sx={{ color: "error.main", mb: 2 }}>{error}</Box>}

              <Field
                as={TextField}
                name="name"
                label="Product Name"
                fullWidth
                margin="normal"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              <Field
                as={TextField}
                name="description"
                label="Description"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Product Banner
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  Upload a banner image for this product (max 10MB). Images will
                  be automatically optimized to 1200px width.
                </Typography>

                {bannerPreview ? (
                  <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ position: "relative" }}>
                      <img
                        src={bannerPreview}
                        alt="Banner preview"
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        size="small"
                        variant="outlined"
                        color="primary"
                        elevation={2}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "primary.light",
                          "&:hover": {
                            bgcolor: "background.default",
                            borderColor: "primary.dark",
                            color: "text.primary",
                          },
                        }}
                        onClick={() => handleRemoveBanner(setFieldValue)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : (
                  <Button
                    component="label"
                    fullWidth
                    sx={{
                      p: 4,
                      flexDirection: "column",
                      textAlign: "center",
                      border: "2px dashed",
                      borderColor: "divider",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        handleBannerUpload(event, setFieldValue)
                      }
                    />

                    <PhotoCamera
                      sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Click to upload banner image
                    </Typography>
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  License Settings
                </Typography>
                <Field
                  name="requires_license"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            This product requires a license
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Users will need to enter a hardware ID and get a
                            license key to use this product
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ProductFormDialog;
